import { useMemo, useState } from 'react';
import { ProfileSection } from '@/components/organisms/ProfileSection';
import { RevenueArpuChart } from '@/components/organisms/RevenueArpuChart';
import { RankBars } from '@/components/organisms/RankBars';
import { SegmentedControl } from '@/components/organisms/SegmentedControl';
import { ContributionChart } from '@/components/organisms/ContributionChart';
import { MekkoChart } from '@/components/organisms/MekkoChart';
import { MiniLineChart } from '@/components/organisms/MiniLineChart';
import { Emph } from '@/components/organisms/ChartSummary';
import { PlatformIcon } from '@/components/primitives';
import { formatArpu, formatUsdCompact, prettyQuarter } from '@/lib/format';
import { colorForRegion } from '@/lib/titleCharts';
import { PROFILE_REGIONS, useRegionHistories } from './usePlatformProfile';
import { revenueGrowthBreakdown } from './revenueGrowth';
import { usePlatformProfileContext } from './PlatformProfileLayout';
import styles from './ProfileTabs.module.css';

const PEER_OPTS = [
  { value: 'revenue', label: 'Revenue' },
  { value: 'arpu', label: 'ARPU' },
];

const signedPct = (v: number) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(1)}%`;

const GROWTH_SERIES = [
  { key: 'subscribers', label: 'Subscribers', color: 'var(--metric-retained)' },
  { key: 'price', label: 'Price (like-for-like)', color: 'var(--accent-secondary)' },
  { key: 'mix', label: 'Regional mix', color: 'var(--data-9)' },
];

/** Revenue — Revenue & ARPU, what drives growth, regional mix, against peers. */
export function RevenueTab() {
  const { id, region, profile } = usePlatformProfileContext();
  const { history, regions, leaderboard, historyAll } = profile;
  const { byRegion } = useRegionHistories(id);
  const [peerMetric, setPeerMetric] = useState<'revenue' | 'arpu'>('revenue');

  const scopeRegions = region ? [region] : [...PROFILE_REGIONS];
  const growth = useMemo(() => revenueGrowthBreakdown(byRegion, scopeRegions), [byRegion, scopeRegions.join('|')]);

  if (historyAll.isLoading) {
    return (
      <div className={styles.tab}>
        <div className={styles.placeholder}>
          <span className={styles.placeholderLabel}>Loading revenue data…</span>
        </div>
      </div>
    );
  }
  if (!history.length) {
    return (
      <div className={styles.tab}>
        <div className={styles.placeholder}>
          <span className={styles.placeholderLabel}>No revenue history available</span>
        </div>
      </div>
    );
  }

  const regionScope = region ?? 'All regions';
  const latestQ = prettyQuarter(history[history.length - 1].quarter);
  const span = `${prettyQuarter(history[0].quarter)} – ${latestQ}`;
  const qs = region ? `?region=${region}` : '';

  // One region has no mix to speak of.
  const growthSeries = region ? GROWTH_SERIES.filter((x) => x.key !== 'mix') : GROWTH_SERIES;

  const regionRows = [...(regions.data ?? [])].sort((a, b) => b.revenue - a.revenue);
  const arpuTrend = (() => {
    const quarters = [...new Set(PROFILE_REGIONS.flatMap((r) => (byRegion[r] ?? []).map((h) => h.quarter)))]
      .sort()
      .slice(-8);
    return quarters.map((q) => {
      const row: Record<string, string | number | undefined> = { label: prettyQuarter(q) };
      for (const r of PROFILE_REGIONS) row[r] = byRegion[r]?.find((h) => h.quarter === q)?.arpu;
      return row;
    });
  })();

  const peers = [...(leaderboard.data ?? [])].sort((a, b) => b[peerMetric] - a[peerMetric]);
  const fmtPeer = peerMetric === 'revenue' ? formatUsdCompact : formatArpu;

  return (
    <div className={styles.tab}>
      <ProfileSection
        title="Revenue & ARPU"
        scope={`${regionScope} · ${span}`}
        hint="Revenue = subscribers × ARPU (streaming metrics). ARPU is average revenue per subscriber."
      >
        <RevenueArpuChart platform={id} history={history} scopeLabel={region ?? 'all regions'} />
      </ProfileSection>

      <ProfileSection
        title="What Drives Revenue Growth"
        scope={`${regionScope} · quarter on quarter`}
        hint={
          region
            ? 'Each quarter\'s revenue growth split into more subscribers and higher revenue per subscriber (price). The dot is total revenue growth.'
            : 'Each quarter\'s revenue growth split three ways: more subscribers; price, the change in ARPU within each region; and regional mix, the change in blended ARPU from subscribers shifting between cheaper and dearer regions. The dot is total revenue growth.'
        }
      >
        <ContributionChart
          points={growth}
          series={growthSeries}
          totalLabel="Revenue growth"
          format={signedPct}
          yTitle="Contribution to Revenue Growth"
          summary={(p) => (
            <>
              <Emph>{id}</Emph> revenue {p.total >= 0 ? 'grew' : 'fell'} <Emph>{signedPct(p.total)}</Emph> in{' '}
              <Emph>{p.label}</Emph>: subscribers <Emph>{signedPct(Number(p.subscribers))}</Emph>, price{' '}
              <Emph>{signedPct(Number(p.price))}</Emph>
              {!region && (
                <>
                  , regional mix <Emph>{signedPct(Number(p.mix))}</Emph>
                </>
              )}
              .
            </>
          )}
        />
      </ProfileSection>

      <ProfileSection
        title="Regional Revenue Mix"
        scope={latestQ}
        hint="Each region's column is as wide as its share of subscribers and as tall as its ARPU, so its area is its share of revenue. The region picked in the header is outlined."
      >
        <MekkoChart
          items={regionRows.map((r) => ({
            key: r.region,
            label: r.region,
            width: r.subscribers,
            height: r.arpu,
            color: colorForRegion(r.region),
            highlight: r.region === region,
          }))}
          widthLabel="Subscribers"
          heightTitle="ARPU"
          formatHeight={formatArpu}
          areaLabel="of revenue"
        />
        {arpuTrend.length > 1 && (
          <div className={styles.trend}>
            <MiniLineChart
              title={`ARPU by region, last ${arpuTrend.length} quarters`}
              points={arpuTrend}
              series={PROFILE_REGIONS.map((r) => ({ key: r, label: r, color: colorForRegion(r) }))}
              format={formatArpu}
            />
          </div>
        )}
      </ProfileSection>

      <ProfileSection
        title="Against Other Platforms"
        scope={`${regionScope} · ${latestQ}`}
        hint="Every platform with streaming metrics, in the same region and quarter."
        controls={
          <SegmentedControl
            variant="field"
            options={PEER_OPTS}
            value={peerMetric}
            onChange={(v) => setPeerMetric(v as 'revenue' | 'arpu')}
          />
        }
      >
        <RankBars
          items={peers.map((p) => ({
            key: p.platform,
            label: p.platform,
            icon: <PlatformIcon name={p.platform} size={20} />,
            value: p[peerMetric],
            display: fmtPeer(p[peerMetric]),
            highlight: p.platform === id,
            href: `/platforms/${encodeURIComponent(p.platform)}/revenue${qs}`,
          }))}
        />
      </ProfileSection>
    </div>
  );
}
